import {Checkbox, Modal, ModalBody, ModalContent, ModalHeader, SelectItem} from "@heroui/react";
import PSelect from "./PSelect.tsx";
import {addToast} from "@heroui/toast";
import {Icon} from "@iconify/react";
import {useState} from "react";
import {signUpResultMessage} from "./events/registrationState.tsx";

import {apiActionImproved} from "../api.ts";
import {useGlobalModal} from "../core/GlobalModal.tsx";
import services from "../services";
import PButton from "./PButton.tsx";
import PInput from "./PInput.tsx";
import CreatePlayerModal from "./modals/CreatePlayerModal.tsx";

type Mode = 'team' | 'solo' | 'find';
type EventType = 'tournament' | 'league';
type Player = {uid: string; name: string; elo_rating?: number};

const JoinTournamentModal = ({
    open,
    onClose,
    onJoined,
    eventUid,
    eventType,
    participantsType,
    viewerName,
    requiresPayment,
    paymentsReady = false,
    divisions = null,
    registrationFields = null,
    waivers = null,
    isTeamSport = false,
    isGuest = false,
}: {
    open: boolean;
    onClose: () => void;
    onJoined: () => void;
    eventUid: string;
    eventType: EventType;
    participantsType?: number | null;
    viewerName?: string;
    requiresPayment?: boolean;
    // Organizer has finished Stripe payout onboarding — if false we register but defer payment.
    paymentsReady?: boolean;
    divisions?: any[] | null;
    registrationFields?: any[] | null;
    waivers?: any[] | null;
    // Team sports (football/basketball/cricket/esports) register a whole team — no doubles partner flow.
    isTeamSport?: boolean;
    // Logged-out team-sport registration: collect team name + contact and register without an account.
    isGuest?: boolean;
}) => {
    const globalModal = useGlobalModal();
    const [mode, setMode] = useState<Mode>('team');
    const [search, setSearch] = useState('');
    const [results, setResults] = useState<Player[] | null>(null);
    const [partner, setPartner] = useState<Player | null>(null);
    const [searching, setSearching] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');

    // Guest (no-login) team registration fields.
    const [teamName, setTeamName] = useState('');
    const [contactName, setContactName] = useState('');
    const [contactEmail, setContactEmail] = useState('');
    const guestRegister = isGuest && isTeamSport;
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim());
    const guestValid = !guestRegister || (teamName.trim() !== '' && emailValid);

    // Registration extras configured by the organizer (tournament divisions / questions / waivers).
    const [division, setDivision] = useState('');
    const [formAnswers, setFormAnswers] = useState<Record<string, string>>({});
    const [waiversAccepted, setWaiversAccepted] = useState<Record<string, boolean>>({});
    const divisionsList = divisions || [];
    const fieldsList = registrationFields || [];
    const waiversList = waivers || [];
    const registrationValid =
        (divisionsList.length === 0 || !!division) &&
        fieldsList.every((f: any) => !f.required || (formAnswers[f.key] || '').trim() !== '') &&
        waiversList.every((w: any) => waiversAccepted[w.key]);
    const registrationExtras = {
        ...(division ? {division} : {}),
        ...(Object.keys(formAnswers).length ? {form_answers: formAnswers} : {}),
        ...(waiversList.length ? {waivers_accepted: Object.keys(waiversAccepted).filter((k) => waiversAccepted[k])} : {}),
    };

    const target = eventType === 'league' ? {league_uid: eventUid} : {tournament_uid: eventUid};
    const isSingles = Number(participantsType) === 3;
    // Both singles and team sports skip the doubles partner flow — a single confirm + register.
    const simpleRegister = isSingles || isTeamSport;

    const runSearch = async () => {
        if (!search.trim()) return;
        setSearching(true);
        try {
            const res = await services.players.search({
                searchText: search.trim(),
                event_partner_tournament_uid: eventType === 'tournament' ? eventUid : undefined,
                event_partner_league_uid: eventType === 'league' ? eventUid : undefined,
            });
            setResults((Array.isArray(res) ? res : []) as Player[]);
        } finally {
            setSearching(false);
        }
    };

    // The server says how the entry is paid (EntryFeeTerms): Checkout now, or on the day. Older
    // answers without the fields fall back to the page's own reading of the event.
    const redirectToCheckoutIfNeeded = async (res: {application_uid?: string; checkout_required?: boolean; pay_on_the_day?: boolean; waitlisted?: boolean} | null | undefined): Promise<boolean> => {
        const applicationUid = res?.application_uid;
        if (!applicationUid || res?.waitlisted) return false;
        const checkout = res?.checkout_required ?? (!!requiresPayment && paymentsReady);
        const onTheDay = res?.pay_on_the_day ?? (!!requiresPayment && !paymentsReady);
        if (!checkout) {
            if (!onTheDay) return false;
            addToast({color: 'success', title: 'Registered — pay the entry fee to the organizer on the day.'});
            onJoined();
            return true;
        }
        let pay: any;
        try {
            pay = await apiActionImproved('stripe-prepare-enroll-payment' as any, {application_uid: applicationUid});
        } catch (err: any) {
            pay = {error: err?.message};
        }
        if (pay?.error || !pay?.redirect_url) {
            // The entry is saved but not paid, so it is not in yet — never report it as registered.
            addToast({color: 'warning', title: `Your entry is saved but not paid yet${pay?.error ? ` (${pay.error})` : ''}. Register again to pay.`});
            onJoined();
            return true;
        }
        document.location.href = pay.redirect_url;
        return true;
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            if (guestRegister) {
                const res = await services.publicEvent.registerGuest({
                    ...target,
                    team_name: teamName.trim(),
                    name: contactName.trim() || teamName.trim(),
                    email: contactEmail.trim(),
                    ...registrationExtras,
                });
                if (await redirectToCheckoutIfNeeded(res)) return;
                addToast({color: 'success', title: `${signUpResultMessage(res)} — check your email for the details`});
                onJoined();
                return;
            }
            if (simpleRegister) {
                const res = await services.publicEvent.apply({...target, ...registrationExtras});
                if (await redirectToCheckoutIfNeeded(res)) return;
                addToast({color: 'success', title: res.waitlisted ? 'Added to the waiting list' : signUpResultMessage(res, !!res.pay_on_the_day)});
                onJoined();
                return;
            }
            if (mode === 'team') {
                if (!partner) return;
                const res = await services.publicEvent.apply({...target, partner_uid: partner.uid, ...registrationExtras});
                if (await redirectToCheckoutIfNeeded(res)) return;
                addToast({color: 'success', title: res.waitlisted ? 'Added to the waiting list' : signUpResultMessage(res, !!res.pay_on_the_day)});
            } else if (mode === 'solo') {
                const res = await services.publicEvent.apply({...target, ...registrationExtras});
                if (await redirectToCheckoutIfNeeded(res)) return;
                addToast({color: 'success', title: res.waitlisted ? 'Added to the waiting list' : `${signUpResultMessage(res, !!res.pay_on_the_day)}. Add a partner later.`});
            } else if (mode === 'find') {
                const res = await services.publicEvent.apply({...target, ...registrationExtras});
                await services.publicEvent.joinPartnerSearch(target);
                if (await redirectToCheckoutIfNeeded(res)) return;
                addToast({color: 'success', title: res.waitlisted ? 'Added to the waiting list and looking for a partner' : `${signUpResultMessage(res, !!res.pay_on_the_day)} — you're listed as looking for a partner`});
            }
            onJoined();
        } catch (err: any) {
            addToast({color: 'danger', title: err?.message || 'Could not register'});
        } finally {
            setSubmitting(false);
        }
    };

    const openInviteByEmail = () => {
        setInviteEmail('');
        setInviteOpen(true);
    };

    const sendInviteEmail = () => {
        const email = inviteEmail.trim();
        if (!email) return;
        const subject = encodeURIComponent('Join my team on ScorePal');
        const body = encodeURIComponent(`Hi,\n\nI'd like to play this event as a team with you on ScorePal. Open this page to sign up:\n${window.location.href}\n\nThanks!`);
        window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
        setInviteOpen(false);
    };

    const openCreatePlayer = () => {
        globalModal.open(
            <CreatePlayerModal
                tournamentUid={eventType === 'tournament' ? eventUid : undefined}
                leagueUid={eventType === 'league' ? eventUid : undefined}
                title="Create Partner"
                onCreated={(p) => {
                    if (p) setPartner({uid: p.uid, name: p.name});
                    globalModal.close();
                }}
            />
        );
    };

    const ctaLabel = isTeamSport ? 'Register Team' : isSingles ? 'Register' : mode === 'team' ? 'Register Team' : mode === 'solo' ? 'Register Solo' : 'Find Partner';
    const ctaDisabled = (!simpleRegister && mode === 'team' && !partner) || !registrationValid || !guestValid || submitting;

    return (
        <>
        <Modal isOpen={open} placement="top" size="2xl" onOpenChange={(o) => { if (!o) onClose() }}>
            <ModalContent>
                <ModalHeader className="flex flex-col gap-0.5">
                    <span>Join {eventType === 'league' ? 'League' : 'Tournament'}</span>
                    {viewerName ? <span className="text-sm font-normal text-default-500">Joining as <span className="font-semibold text-default-700">{viewerName}</span></span> : null}
                </ModalHeader>
                <ModalBody className="pb-6">
                    {guestRegister ? (
                        <div className="space-y-3">
                            <div className="rounded-xl bg-default-50 p-4 text-sm text-default-600">
                                Register your team for this {eventType === 'league' ? 'league' : 'tournament'}.
                                We'll email a confirmation with all the details{requiresPayment ? ' once payment is complete' : ''}.
                            </div>
                            <PInput
                                label="Team name"
                                labelPlacement="outside"
                                placeholder="e.g. Riverside Rovers"
                                isRequired
                                value={teamName}
                                onChange={(e) => setTeamName(e.target.value)}
                                startContent={<Icon icon="material-symbols-light:groups-outline" className="text-default-400 text-xl" />}
                            />
                            <PInput
                                label="Your name"
                                labelPlacement="outside"
                                placeholder="Contact name"
                                value={contactName}
                                onChange={(e) => setContactName(e.target.value)}
                                startContent={<Icon icon="material-symbols-light:person-outline" className="text-default-400 text-xl" />}
                            />
                            <PInput
                                label="Email"
                                labelPlacement="outside"
                                placeholder="name@example.com"
                                type="email"
                                isRequired
                                value={contactEmail}
                                onChange={(e) => setContactEmail(e.target.value)}
                                startContent={<Icon icon="material-symbols-light:mail-outline" className="text-default-400 text-xl" />}
                            />
                            {contactEmail.trim() !== '' && !emailValid && (
                                <p className="text-xs text-danger">Please enter a valid email address.</p>
                            )}
                        </div>
                    ) : simpleRegister ? (
                        <div className="rounded-xl bg-default-50 p-4 text-sm text-default-600">
                            {isTeamSport
                                ? <>Register your team for this {eventType === 'league' ? 'league' : 'tournament'}. You can manage your squad afterwards.</>
                                : <>Confirm registration for this {eventType === 'league' ? 'league' : 'tournament'}.</>}
                        </div>
                    ) : (
                    <div>
                        <div className="mb-2 text-sm font-bold">How would you like to join?</div>
                        <div className="grid gap-3 sm:grid-cols-3">
                            <ChoiceCard
                                icon="material-symbols-light:group"
                                title="As Team"
                                subtitle="With partner"
                                active={mode === 'team'}
                                onClick={() => setMode('team')}
                            />
                            <ChoiceCard
                                icon="material-symbols-light:person-add"
                                title="Solo"
                                subtitle="Add partner later"
                                active={mode === 'solo'}
                                onClick={() => { setMode('solo'); setPartner(null) }}
                            />
                            <ChoiceCard
                                icon="material-symbols-light:person-search-outline"
                                title="Find Partner"
                                subtitle="Looking for partner"
                                active={mode === 'find'}
                                onClick={() => { setMode('find'); setPartner(null) }}
                            />
                        </div>
                    </div>
                    )}

                    {!simpleRegister && mode === 'team' ? (
                        <div className="mt-2">
                            <div className="mb-1 text-sm font-bold">Find your partner</div>
                            {partner ? (
                                <div className="flex items-center justify-between rounded-xl border border-primary bg-primary/5 p-3">
                                    <div className="flex items-center gap-3">
                                        <div className="grid h-9 w-9 place-items-center rounded-full bg-primary text-white font-black">
                                            {partner.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <div className="text-sm font-bold">{partner.name}</div>
                                            {partner.elo_rating ? <div className="text-xs text-default-500">ELO {partner.elo_rating}</div> : null}
                                        </div>
                                    </div>
                                    <PButton size="sm" variant="light" onPress={() => setPartner(null)}>Change</PButton>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center gap-2">
                                        <PInput
                                            placeholder="Search by name or email..."
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            onKeyDown={(e: any) => { if (e.key === 'Enter') { e.preventDefault(); runSearch() } }}
                                            startContent={<Icon icon="material-symbols-light:search" className="text-default-400 text-xl" />}
                                        />
                                        <PButton color="primary" onPress={runSearch} isLoading={searching}>Search</PButton>
                                    </div>

                                    {results === null ? (
                                        <div className="mt-3 rounded-xl bg-default-50 p-4 text-sm text-default-500 text-center">
                                            Type a name or email to find your partner
                                        </div>
                                    ) : results.length === 0 ? (
                                        <div className="mt-3 rounded-xl bg-default-50 p-4 text-sm text-default-500 text-center">
                                            No players matched "{search}". Use the options below to invite them.
                                        </div>
                                    ) : (
                                        <div className="mt-3 max-h-56 overflow-y-auto rounded-xl border border-default-200">
                                            {results.map((p) => (
                                                <button
                                                    key={p.uid}
                                                    type="button"
                                                    onClick={() => setPartner(p)}
                                                    className="flex w-full items-center justify-between border-b border-default-100 p-3 text-left last:border-b-0 hover:bg-default-50"
                                                >
                                                    <span className="text-sm font-medium">{p.name}</span>
                                                    {p.elo_rating ? <span className="text-xs text-default-500">ELO {p.elo_rating}</span> : null}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-default-500">
                                        <span>Can't find them?</span>
                                        <button type="button" onClick={openInviteByEmail} className="font-bold text-primary hover:underline">
                                            Invite by email
                                        </button>
                                        <span>·</span>
                                        <button type="button" onClick={openCreatePlayer} className="font-bold text-primary hover:underline">
                                            Create new player
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : !simpleRegister && mode === 'solo' ? (
                        <div className="mt-2 rounded-xl bg-default-50 p-4 text-sm text-default-600">
                            You'll be registered on your own. You can add a partner later from the event page.
                        </div>
                    ) : !simpleRegister ? (
                        <div className="mt-2 rounded-xl bg-default-50 p-4 text-sm text-default-600">
                            You'll be registered and also listed in "Looking for a partner" so the organizer or other players can pair you up.
                        </div>
                    ) : null}

                    {/* Organizer-configured registration extras: division / questions / waivers */}
                    {divisionsList.length > 0 && (
                        <PSelect className="mt-2" label="Division" isRequired placeholder="Select your division"
                                 selectedKeys={division ? [division] : []}
                                 onSelectionChange={(e: any) => setDivision(e.currentKey || '')}>
                            {divisionsList.filter((d: any) => d.name).map((d: any) => (
                                <SelectItem key={d.key || d.name} textValue={d.name}>{d.name}</SelectItem>
                            ))}
                        </PSelect>
                    )}
                    {fieldsList.map((f: any) => (
                        f.type === 'select'
                            ? <PSelect key={f.key} className="mt-2" label={f.label} isRequired={!!f.required}
                                       selectedKeys={formAnswers[f.key] ? [formAnswers[f.key]] : []}
                                       onSelectionChange={(e: any) => setFormAnswers({...formAnswers, [f.key]: e.currentKey || ''})}>
                                {(f.options || []).map((o: string) => (
                                    <SelectItem key={o} textValue={o}>{o}</SelectItem>
                                ))}
                            </PSelect>
                            : <PInput key={f.key} className="mt-2" label={f.label} isRequired={!!f.required}
                                      value={formAnswers[f.key] || ''}
                                      onChange={(e) => setFormAnswers({...formAnswers, [f.key]: e.target.value})}/>
                    ))}
                    {waiversList.map((w: any) => (
                        <div key={w.key} className="mt-2 rounded-xl border border-default-200 p-3">
                            <div className="text-sm font-semibold mb-1">{w.title || 'Waiver'}</div>
                            {w.text && <div className="text-xs text-default-500 max-h-28 overflow-y-auto whitespace-pre-wrap mb-2">{w.text}</div>}
                            <Checkbox size="sm" isSelected={!!waiversAccepted[w.key]}
                                      onValueChange={(v) => setWaiversAccepted({...waiversAccepted, [w.key]: v})}>
                                <span className="text-sm">I have read and accept this waiver</span>
                            </Checkbox>
                        </div>
                    ))}

                    <div className="mt-4 flex items-center justify-between gap-3">
                        <PButton variant="light" onPress={onClose}>Skip</PButton>
                        <PButton color="primary" className="px-8" onPress={handleSubmit} isDisabled={ctaDisabled} isLoading={submitting}>
                            {ctaLabel}
                        </PButton>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>

        <Modal isOpen={inviteOpen} placement="center" size="md" onOpenChange={(o) => { if (!o) setInviteOpen(false) }}>
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-0.5">
                        <span>Invite by email</span>
                        <span className="text-sm font-normal text-default-500">We'll open your email app with a pre-filled invite for your partner.</span>
                    </ModalHeader>
                    <ModalBody className="pb-6">
                        <PInput
                            label="Partner's email"
                            labelPlacement="outside"
                            placeholder="name@example.com"
                            type="email"
                            autoFocus
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            onKeyDown={(e: any) => { if (e.key === 'Enter' && inviteEmail.trim()) { e.preventDefault(); sendInviteEmail() } }}
                            startContent={<Icon icon="material-symbols-light:mail-outline" className="text-default-400 text-xl" />}
                        />
                        <div className="mt-4 flex justify-end gap-2">
                            <PButton variant="light" onPress={() => setInviteOpen(false)}>Cancel</PButton>
                            <PButton color="primary" onPress={sendInviteEmail} isDisabled={!inviteEmail.trim()}>
                                Send invite
                            </PButton>
                        </div>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

function ChoiceCard({icon, title, subtitle, active, onClick}: {
    icon: string; title: string; subtitle: string; active: boolean; onClick: () => void
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex flex-col items-center gap-1 rounded-2xl border-2 p-4 text-center transition ${
                active ? 'border-primary bg-primary/5' : 'border-default-200 bg-default-50 hover:border-default-300'
            }`}
        >
            <Icon icon={icon} className={`text-3xl ${active ? 'text-primary' : 'text-default-500'}`} />
            <div className={`text-sm font-bold ${active ? 'text-primary' : 'text-default-700'}`}>{title}</div>
            <div className="text-xs text-default-500">{subtitle}</div>
        </button>
    );
}

export default JoinTournamentModal;
